/**
 * ClubSettingsHubPage - Club Settings Navigation Hub
 * Issue #871: Comprehensive club settings with categorized sub-pages
 *
 * Pattern matches SettingsPage.tsx hub design
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TopBar } from '../../components';
import { useClub } from '../../hooks/useClubs';
import { usePendingMembershipRequests } from '../../hooks/useMembershipRequests';
import {
  Building2,
  Shield,
  Users,
  Link2,
  AlertTriangle,
  ChevronRight,
  Calendar,
  CalendarDays,
  UserPlus,
} from 'lucide-react';

interface SettingsCardProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  badge?: number;
  onClick: () => void;
}

function SettingsCard({ icon, label, description, badge, onClick }: SettingsCardProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl hover:bg-oro-surface-alt transition-colors text-left"
    >
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-oro-surface-alt text-text-secondary">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-text-primary">{label}</p>
          {badge !== undefined && badge > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-accent text-white rounded-full min-w-[1.25rem] text-center">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </div>
        {description && (
          <p className="text-sm text-text-muted truncate">{description}</p>
        )}
      </div>
      <ChevronRight className="w-5 h-5 text-text-muted" />
    </button>
  );
}

export default function ClubSettingsHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: clubData, isLoading, error } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isOwner = membership?.is_owner || false;

  // Fetch pending membership requests for badge (#1239)
  const { data: requestsData } = usePendingMembershipRequests(club?.id || '');
  const pendingRequestCount = requestsData?.requests?.length || 0;

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar
          title="Club Settings"
          onBack={() => navigate(`/clubs/${slug}`)}
        />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !isOwner || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar
          title="Club Settings"
          onBack={() => navigate(`/clubs/${slug}`)}
        />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {error ? 'Unable to load settings' : 'Only club owners can access settings'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Club Settings"
        onBack={() => navigate(`/clubs/${slug}`)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">

          {/* Club Header */}
          <div className="flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl">
            <div className="w-12 h-12 rounded-full bg-oro-surface-alt flex items-center justify-center overflow-hidden">
              {club.avatar_image_url ? (
                <img
                  src={club.avatar_image_url}
                  alt={club.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-6 h-6 text-text-muted" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-text-primary truncate">{club.name}</h2>
              <p className="text-sm text-text-muted">Owner</p>
            </div>
          </div>

          {/* GENERAL Section */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              General
            </h2>
            <div className="space-y-3">
              <SettingsCard
                icon={<Building2 className="w-5 h-5" />}
                label="Club Details"
                description="Name, avatar, location, links"
                onClick={() => navigate(`/clubs/${slug}/settings/general`)}
              />
              <SettingsCard
                icon={<Calendar className="w-5 h-5" />}
                label="Calendar Feed"
                description="Subscribe to club events in your calendar"
                onClick={() => navigate(`/clubs/${slug}/settings/calendar`)}
              />
              <SettingsCard
                icon={<CalendarDays className="w-5 h-5" />}
                label="Event Defaults"
                description="Default values for new events"
                onClick={() => navigate(`/clubs/${slug}/settings/events`)}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* PRIVACY & ACCESS Section */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Privacy & Access
            </h2>
            <div className="space-y-3">
              <SettingsCard
                icon={<Shield className="w-5 h-5" />}
                label="Visibility & Invites"
                description="Club visibility, invite permissions"
                onClick={() => navigate(`/clubs/${slug}/settings/privacy`)}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* MEMBERS Section */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Members
            </h2>
            <div className="space-y-3">
              <SettingsCard
                icon={<Users className="w-5 h-5" />}
                label="Manage Members"
                description="Roles, permissions, remove members"
                onClick={() => navigate(`/clubs/${slug}/members`)}
              />
              <SettingsCard
                icon={<Link2 className="w-5 h-5" />}
                label="Manage Invites"
                description="View, create, revoke invite links"
                onClick={() => navigate(`/clubs/${slug}/settings/invites`)}
              />
              <SettingsCard
                icon={<UserPlus className="w-5 h-5" />}
                label="Membership Requests"
                description="Review pending join requests"
                badge={pendingRequestCount}
                onClick={() => navigate(`/clubs/${slug}/settings/requests`)}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* DANGER ZONE */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Danger Zone
            </h2>
            <div className="space-y-3">
              <SettingsCard
                icon={<AlertTriangle className="w-5 h-5 text-oro-warning" />}
                label="Danger Zone"
                description="Transfer ownership, delete club"
                onClick={() => navigate(`/clubs/${slug}/settings/danger`)}
              />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
